import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "axios";
import validator from "validator";
import { useSelector } from "react-redux";

export const PackageCompletionNotificationRecipients = ({
  recipients,
  setManagementData,
  isSm,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const [email, setEmail] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [message, setMessage] = useState({ severity: "success", text: "" });

  const rows = useMemo(
    () =>
      (recipients || []).map((recipient, index) => ({
        id: recipient?.id || String(index),
        email: recipient?.email || "",
      })),
    [recipients],
  );

  const refreshManagementData = async () => {
    const response = await axios.post(
      `${process.env.REACT_APP_BASE_URL}/nocApi/data/managementData`,
      { currentSession },
      { withCredentials: true },
    );
    setManagementData(response?.data?.data);
  };

  const handleAdd = async () => {
    const normalizedEmail = email.trim().toLowerCase();
    if (!validator.isEmail(normalizedEmail)) {
      setMessage({ severity: "error", text: "Enter a valid email address." });
      return;
    }
    if (rows.some((row) => row.email.toLowerCase() === normalizedEmail)) {
      setMessage({ severity: "error", text: "Email address already exists." });
      return;
    }

    try {
      setAddLoading(true);
      setMessage({ severity: "success", text: "" });
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/package-completion-notification-recipients/add`,
        { currentSession, email: normalizedEmail },
        { withCredentials: true },
      );
      await refreshManagementData();
      setEmail("");
      setMessage({
        severity: "success",
        text: response?.data?.message || "Recipient added.",
      });
    } catch (error) {
      setMessage({
        severity: "error",
        text: error?.response?.data?.message || "Couldn't add recipient.",
      });
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!selectedRow?.email) return;
    try {
      setRemoveLoading(true);
      setMessage({ severity: "success", text: "" });
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/package-completion-notification-recipients/remove`,
        { currentSession, email: selectedRow.email },
        { withCredentials: true },
      );
      await refreshManagementData();
      setConfirmOpen(false);
      setSelectedRow(null);
      setMessage({
        severity: "success",
        text: response?.data?.message || "Recipient removed.",
      });
    } catch (error) {
      setMessage({
        severity: "error",
        text: error?.response?.data?.message || "Couldn't remove recipient.",
      });
    } finally {
      setRemoveLoading(false);
    }
  };

  const columns = [
    { field: "email", headerName: "Email Address", flex: 1, minWidth: 280 },
    {
      field: "actions",
      type: "actions",
      headerName: "",
      width: 80,
      getActions: (params) => [
        <GridActionsCellItem
          key={`remove-${params.id}`}
          icon={<DeleteOutlineIcon color="error" />}
          label="Remove"
          disabled={addLoading || removeLoading}
          onClick={() => {
            setSelectedRow(params.row);
            setConfirmOpen(true);
          }}
        />,
      ],
    },
  ];

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These addresses receive enabled signature-completion and NOC-recording
        notifications for all customers and packages. An empty list is allowed
        and safely sends no internal notifications.
      </Typography>

      <DataGrid
        rows={rows}
        columns={columns}
        pageSizeOptions={[5, 10, 25]}
        initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
        disableRowSelectionOnClick
        sx={{ minHeight: "18rem" }}
      />

      <Grid container spacing={isSm ? 1 : 2} alignItems="center" sx={{ mt: 2 }}>
        <Grid size={{ xs: 12, md: 9 }}>
          <TextField
            label="Notification recipient email"
            type="email"
            variant="filled"
            fullWidth
            value={email}
            disabled={addLoading || removeLoading}
            onChange={(event) => setEmail(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && email && !addLoading) {
                event.preventDefault();
                handleAdd();
              }
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <LoadingButton
            fullWidth
            variant="contained"
            loading={addLoading}
            disabled={!email.trim() || removeLoading}
            onClick={handleAdd}
          >
            Add Recipient
          </LoadingButton>
        </Grid>
      </Grid>

      {message.text && (
        <Alert
          severity={message.severity}
          onClose={() => setMessage({ severity: "success", text: "" })}
          sx={{ mt: 2 }}
        >
          {message.text}
        </Alert>
      )}

      <Dialog
        open={confirmOpen}
        onClose={removeLoading ? undefined : () => setConfirmOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Remove notification recipient?</DialogTitle>
        <DialogContent>
          <Typography>
            Remove <strong>{selectedRow?.email}</strong> from global package
            completion notifications?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            disabled={removeLoading}
            onClick={() => setConfirmOpen(false)}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            loading={removeLoading}
            onClick={handleRemove}
          >
            Remove
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
